import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link2, Video, Mic, Upload } from 'lucide-react';
import { meetingPollDecision, meetingCanStop, meetingCanStartNew, describeJoinError } from '../../lib/meetBot';
import { PHASES, nextPhase, shouldResetOnRequest } from '../../lib/recognition/machine';
import { routeLink, extractLink } from '../../lib/recognition/routeLink';
import { useMeetingPlatforms } from '../../hooks/useMeetingPlatforms';
import { analyzeChannels, shouldSplitByChannels } from '../../lib/recognition/stereo';
import { fileSource } from '../../lib/recognition/sources/file';
import { stereoSource } from '../../lib/recognition/sources/stereo';
import { meetingSource } from '../../lib/recognition/sources/meeting';
import { liveSource } from '../../lib/recognition/sources/live';
import { linkSource } from '../../lib/recognition/sources/link';
import { fetchService } from '../../services/fetchService';
import { transcribeService } from '../../services/transcribeService';
import { CancelledError, isCancelled } from '../../lib/recognition/cancel';
import { resolveQuotaBlockedError } from '../../lib/quotaError';
import { NO_SPEECH_MESSAGE, resultIsEmpty } from '../../lib/noSpeech';
import { unavailableText } from '../../lib/recognition/unavailable';
import { getAudioDuration } from '../../utils/audioDuration';
import { buildDownloadName } from '../../utils/downloadName';
import { extractAudio } from '../../lib/audioExtract';
import { voiceQuestions } from '../../lib/recognition/voiceId';
import { protocolHandoff } from '../../lib/recognition/protocolHandoff';
import { formatTimecodeHMMSS } from '../../FormatTime';
import { voiceService } from '../../services/voiceService';
import StreamingTranscription from '../StreamingTranscription';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SUPPORTED_VIDEO_PLATFORMS, VIDEO_PLATFORMS_HINT } from '../../lib/videoPlatforms';
import { authService } from '../../services/authService';
import { metricsService } from '../../services/metricsService';
import { ENDPOINTS } from '../../config/endpoints';
import { quotaEnforced, quotaLeftMinutes } from '../../lib/quota';
import ReadyState from './ReadyState';
import MeetingState from './MeetingState';
import ProcessingState from './ProcessingState';
import ResultState from './ResultState';
import PackState from './PackState';
import SpeakerSettings from './SpeakerSettings';
import { packItems, updateItem, removeItem, packSummary } from '../../lib/recognition/pack';
import { loadSpeakerSettings, saveSpeakerSettings } from '../../lib/recognition/settingsPreference';
import { makePostUpload } from './postUpload';
import { useBotName } from '../../hooks/useBotName';
import { CALENDAR_BOT_EMAIL, showCalendarHint } from '../../lib/calendarInvite';
import { leaveLiveAllowed } from '../../lib/streamingHistory';

/**
 * Единый экран «Распознавание»: файл, ссылка на видео, встреча с ботом,
 * стерео-звонок и микрофон — один вход вместо трёх вкладок.
 *
 * Спека: docs/superpowers/specs/2026-08-31-recognition-screen-design.md
 * Макет: docs/redesign/design_handoff_newton/recognition-sprint3-mockup.html
 *
 * Фаза живёт в стейт-машине, а не в наборе булевых флагов: набор флагов
 * допускает состояния вроде «идёт обработка и показан результат», а машина нет.
 * Источники приходят пропсом `sources` — так экран проверяется без сети.
 */

// Фича-флаг сборки, по умолчанию ВЫКЛЮЧЕН. Сравнение с 'true', а не
// отрицание: забытая переменная должна означать «фичи не видно», а не
// «фича сломана у всех» (как REACT_APP_MEETBOT_ENABLED).
// 🪤 REACT_APP_* вшиваются на этапе СБОРКИ: включение — строка в $ENV_prod
// плюс пересборка фронта, а не перезапуск контейнера.
export const RECOGNITION_V2 = process.env.REACT_APP_RECOGNITION_V2 === 'true';

const FORMATS = 'mp3 · wav · m4a · aac · mp4 · mkv';

/** Таймкод реплики для текста Истории: «[0:00:01 - 0:00:05]», как у старого экрана.
 *
 * У плоской расшифровки и ссылки времени числом нет, только строки из ответа
 * сервиса — конец отрезка берётся из `endTimecode`. Без него в Историю уходило
 * одно начало, а завершитель bit-history (render.py) пишет отрезок целиком:
 * эталон — bit-history/tests/fixtures/render_parity.json.
 */
function fragmentTimecode(f) {
  if (typeof f.start === 'number') {
    const end = typeof f.end === 'number' ? ` - ${formatTimecodeHMMSS(f.end)}` : '';
    return `[${formatTimecodeHMMSS(f.start)}${end}]`;
  }
  if (!f.timecode) return null;
  return f.endTimecode ? `[${f.timecode} - ${f.endTimecode}]` : `[${f.timecode}]`;
}

/** Плоский текст расшифровки — то, что уходит в Историю.
 *
 * 🔴 С таймкодами. Отрезков с временем запись в Истории не хранит, только этот
 * текст: без меток в нём время терялось насовсем, и подобрать имена участников
 * по записи было не с чем (обращение 20ac06b8, 02.10.2026). Таймкод стоит до
 * двоеточия — там его ждут разбор (`transcriptParse.LINE_RE`), переименование
 * участников и «кто сколько говорил» по времени (`speakerShares`).
 */
export function transcriptText(result) {
  return (result?.fragments || [])
    .map((f) => {
      const head = [f.speaker || null, fragmentTimecode(f)].filter(Boolean).join(' ');
      return head ? `${head}: ${f.text}` : f.text;
    })
    .join('\n');
}
const MEETING_POLL_MS = 3000;

const post = (url, body, opts = {}) =>
  axios.post(url, body, { ...opts, headers: authService.getAuthHeaders() });
const get = (url) => axios.get(url, { headers: authService.getAuthHeaders() });
// Старты с файлом (file/stereo) идут отдельным кредом — часовым токеном
// назначения upload, а не access-токеном общего post (см. postUpload.js).
const postUpload = makePostUpload();

const DEFAULT_SOURCES = {
  link: linkSource,
  file: fileSource,
  stereo: stereoSource,
  meeting: meetingSource,
  live: liveSource,
  fetch: fetchService,
  extractAudio,
  // Снятие задачи на сервере — тем же вызовом, что и в прежнем экране.
  cancelTask: transcribeService.cancelTask,
  // Извлечение звука — за флагом учётки, как и в прежнем экране.
  canExtract: () => !!authService.getTokenData?.()?.audio_extract_enabled,
  // Длительность из метаданных (без декодирования) решает, разбирать ли каналы
  // вообще. Известную длительность передаёт вызыватель, чтобы не читать её дважды.
  // Декодируем в 8 кГц: для доли «слышна одна сторона» по 50-мс кадрам этого
  // хватает, и результат держится в памяти вшестеро меньше. Пик это не снижает —
  // браузер всё равно сначала разворачивает файл в исходной частоте (stereo.js).
  analyze: async (file, { duration } = {}) => analyzeChannels(file, {
    duration: duration ?? await getAudioDuration(file).catch(() => null),
    decode: async (buffer) => {
      if (window.OfflineAudioContext) {
        return new window.OfflineAudioContext(2, 1, 8000).decodeAudioData(buffer);
      }
      // Safari до 14.1 знает только webkit-контексты. Обычный контекст закрываем:
      // Safari держит их не больше нескольких, дальше конструктор падает, и каждый
      // следующий файл молча читался бы как «каналы неизвестны».
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      try {
        return await ctx.decodeAudioData(buffer);
      } finally {
        ctx.close?.();
      }
    },
  }),
};

// Карточка способа записи. По артборду: 128px высотой, иконка сверху, название
// и пояснение внизу, скругление 16.
//
// На телефоне («Бумага», мобильный макет) плитка становится строкой: иконка
// слева, подпись короче, стрелка справа, высота от 76px.
const ActionCard = ({ Icon, title, description, short, onClick, className = '' }) => {
  // Одна подпись в DOM, а не две с переключением через CSS: тест держит
  // список площадок ровно одним текстом, как и читалка экрана.
  const isMobile = useIsMobile();
  return (
  <button type="button" onClick={onClick}
    className={`min-h-[76px] rounded-[18px] px-5 py-[18px] flex flex-row items-center gap-[14px]
                md:h-[128px] md:min-h-0 md:rounded-panel md:px-[22px] md:pt-[22px] md:pb-5 md:flex-col md:items-start md:justify-between md:gap-0
                bg-paper-card text-left hover:bg-paper-panel transition-colors ${className}`}>
    <Icon size={22} strokeWidth={1.75} className="shrink-0 text-paper-ink" />
    <span className="flex-1 min-w-0 md:flex-none">
      <span className="block text-[16px] font-semibold tracking-[-0.01em] text-paper-ink">{title}</span>
      <span className="mt-[2px] md:mt-[3px] block text-[12px] text-paper-ink-3">
        {isMobile && short ? short : description}
      </span>
    </span>
    <span aria-hidden="true" className="md:hidden text-paper-ink-3">›</span>
  </button>
  );
};

// Подсказка в форме бота: того же бота можно позвать заранее приглашением в
// Outlook. Только в открытой форме — там, где человек уже думает о боте;
// подпись карточки на главном экране не трогаем (решение Владимира 19.10.2026).
const CalendarInviteHint = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    // Без буфера (http, запрет браузера) «Скопировано» не пишем: адрес и так
    // на виду, его можно выделить руками.
    try {
      await navigator.clipboard.writeText(CALENDAR_BOT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* остаётся «Скопировать адрес» */ }
  };
  return (
    <div className="w-full mt-1 rounded-control bg-paper-panel px-[14px] py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
      <p className="m-0 flex-1 min-w-[240px] text-[13px] leading-[1.5] text-paper-ink-2">
        <b className="text-paper-ink">Можно заранее:</b> добавьте{' '}
        <span className="font-mono text-paper-ink">{CALENDAR_BOT_EMAIL}</span> участником встречи в Outlook —
        бот примет приглашение и придёт к началу, запись ляжет в вашу Историю. Не нужен — уберите его из участников.
      </p>
      <button type="button" onClick={copy}
        className="rounded-chip bg-paper-card hover:bg-paper-bg px-3 py-2 text-[12px] font-semibold text-paper-ink transition-colors">
        {copied ? 'Скопировано' : 'Скопировать адрес'}
      </button>
    </div>
  );
};

// «Авто» — язык решает bit-fetch: дорожка у площадки, иначе название записи.
const LINK_LANGUAGES = [['auto', 'Авто'], ['ru', 'Русский'], ['en', 'Английский'],
                        ['other', 'Другой']];

/**
 * Пустое состояние по «Бумаге» (артборд «Распознавание», состояние EMPTY):
 * большая карточка приёма файла, под ней три способа записи плитками, а поле
 * ввода открывается инлайн-полосой под ними — своё у ссылки на видео и своё
 * у встречи с ботом.
 *
 * «Несколько файлов» — тот же скрытый input с `multiple`: выбор больше
 * одного файла уводит в пакет (PackState), одного — в прежний путь.
 */
const EmptyState = ({ url, onUrlChange, onPickFile, onJoinMeeting, onStartLive, onStartLink,
                     onOpenLinkForm, linkFormOpen, onCloseLinkForm,
                     linkSettings, onLinkSettingsChange,
                     meetingUrl, onMeetingUrlChange, onSendBot, onCloseMeetingForm, starting,
                     historyEnabled, botName, joining, calendarHint }) => {
  // Телефон: главное — запись с микрофона (решение владельца 27.09): крупной
  // кнопкой «Записать сейчас», ниже строками бот на встречу, файл и ссылка.
  const isMobile = useIsMobile();
  // Площадки встреч — включённые в админке сейчас (GET /meetbot-api/platforms),
  // до ответа и при сбое — запасной список из routeLink.js.
  const { text: platformsText, short: platformsShort } = useMeetingPlatforms();
  const linkForm = (
    <>
    {/* Инлайн-форма по артборду: поле, действие и крестик одной полосой.
          🔴 Поле ссылки на видео обязано открываться этой же кнопкой, а не
          прятаться навсегда: путь «распознать по ссылке» уже уезжал в прод
          неработающим 31.08.2026, и тест держит его достижимым. */}
      {linkFormOpen && (
        <div className="shrink-0 bg-paper-card rounded-panel px-[18px] py-4 flex flex-col gap-3">
          <div className="flex flex-wrap md:flex-nowrap items-center gap-3">
            <input type="url" value={url} onChange={(e) => onUrlChange(e.target.value)}
              aria-label="Ссылка на видео" placeholder={VIDEO_PLATFORMS_HINT} autoFocus
              className="basis-full md:basis-auto flex-1 min-w-0 rounded-control bg-paper-bg px-[14px] py-3 text-[14px] text-paper-ink placeholder:text-paper-hint outline-none" />
            <button type="button" onClick={onStartLink} disabled={!url.trim() || starting}
              className="rounded-control bg-paper-accent hover:bg-paper-accent-hover px-[18px] py-3 text-[14px] font-semibold text-white transition-colors disabled:bg-paper-bg disabled:text-paper-hint">
              {starting ? 'Запускаем…' : 'Распознать по ссылке'}
            </button>
            <button type="button" onClick={onCloseLinkForm} aria-label="Закрыть"
              className="px-1 text-[18px] text-paper-ink-3 hover:text-paper-ink">×</button>
          </div>
          {/* «По участникам» — тот же выбор, что у файла, и до старта: после
              скачивания bit-fetch уже отдал запись тому или другому сервису
              (обращение fe94f8a8). */}
          <div className="flex flex-wrap items-center gap-x-[22px] gap-y-2">
            <SpeakerSettings id="link-diarize" settings={linkSettings}
              onChange={onLinkSettingsChange} />
            {/* Язык решает, какая модель слушает: русский — GigaAM-v3, другой —
                parakeet. С галочкой выбора нет — участников разделяем только
                в русской речи. */}
            {!linkSettings.diarize && (
              <span className="flex items-center gap-2 text-[13px] text-paper-ink-2">
                Язык:
                <select aria-label="Язык записи" value={linkSettings.language || 'auto'}
                  onChange={(e) => onLinkSettingsChange({ ...linkSettings, language: e.target.value })}
                  className="appearance-none rounded-badge bg-paper-bg px-[6px] py-[3px] text-[13px] font-semibold text-paper-ink outline-none">
                  {LINK_LANGUAGES.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </span>
            )}
            {/* 🔴 Участников разделяет GigaAM — только русская речь; parakeet без
                галочки знает 25 языков. Молча испортить английский вебинар хуже,
                чем сказать об этом заранее. */}
            {linkSettings.diarize && (
              <p className="m-0 w-full text-[12px] text-paper-ink-3">
                По участникам распознаём только русскую речь. Запись на другом языке — без галочки.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
  const meetingForm = (
    <>
    {meetingUrl !== null && (
        <div className="shrink-0 bg-paper-card rounded-panel px-[18px] py-4 flex flex-wrap items-center gap-3">
          <input type="url" value={meetingUrl} aria-label="Ссылка на встречу"
            onChange={(e) => onMeetingUrlChange(e.target.value)} autoFocus
            placeholder="https://telemost.yandex.ru/j/… или https://<пространство>.ktalk.ru/…"
            className="min-w-[280px] flex-1 rounded-control bg-paper-bg px-[14px] py-3 text-[14px] text-paper-ink placeholder:text-paper-hint outline-none" />
          {botName?.needsName && (
            <input type="text" value={botName.name} aria-label="Как подписать бота"
              onChange={(e) => botName.setName(e.target.value)} maxLength={80}
              placeholder="Ваше имя — бот подпишется «Бит.Ньютон, Имя»"
              className="min-w-[220px] flex-1 rounded-control bg-paper-bg px-[14px] py-3 text-[14px] text-paper-ink placeholder:text-paper-hint outline-none" />
          )}
          <button type="button" onClick={onSendBot} disabled={!meetingUrl.trim() || joining}
            className="rounded-control bg-paper-accent hover:bg-paper-accent-hover px-[18px] py-3 text-[14px] font-semibold text-white transition-colors disabled:bg-paper-bg disabled:text-paper-hint">
            {historyEnabled ? 'Отправить бота' : 'Включить Историю и отправить'}
          </button>
          <button type="button" onClick={onCloseMeetingForm} aria-label="Закрыть"
            className="px-1 text-[18px] text-paper-ink-3 hover:text-paper-ink">×</button>
          {/* 🔴 Предупреждение ДО нажатия, а не отказ после. Бот кладёт результат
              только в Историю, с выключенной bit-meetbot заявку не берёт вовсе
              (app/main.py:263) — и до 19.09.2026 человек узнавал об этом отказом,
              когда встреча уже шла, а исправить мог только уходом в профиль. */}
          {!historyEnabled && (
            <p className="m-0 w-full text-[12px] text-paper-accent">
              История выключена — записи встречи сохранять некуда, и бот на неё не выйдет.
              Кнопка включит Историю и позовёт его.
            </p>
          )}
          <p className="m-0 w-full text-[12px] text-paper-ink-3">
            Бот зайдёт гостем и объявит о записи. Поддержаны {platformsText}.
            {botName?.needsName && ' Имя сохраним в профиле; без него бот подпишется просто «Бит.Ньютон».'}
          </p>
          {/* При выключенной Истории бот не выйдет и по приглашению (bit-meetbot
              отказывает history_disabled) — строкой выше уже сказано почему. */}
          {calendarHint && historyEnabled && <CalendarInviteHint />}
        </div>
      )}
    </>
  );
  if (isMobile) {
    // Выбранный способ с формой встаёт первым, остальные уходят под него
    // строками — крупная карточка записи тоже (просьба владельца 27.09: форма
    // открывалась под всеми вариантами, внизу экрана).
    const meetingRow = (
      <ActionCard Icon={Video} title="Позвать бота на встречу"
        description={platformsShort}
        onClick={onJoinMeeting} />
    );
    const fileRow = (
      <ActionCard Icon={Upload} title="Выбрать файл"
        description="Запись с телефона или голосовое из мессенджера"
        onClick={onPickFile} />
    );
    const linkRow = (
      <ActionCard Icon={Link2} title="Ссылка на видео"
        description="YouTube, Rutube, Kinescope…"
        onClick={onOpenLinkForm} />
    );
    const active = meetingUrl !== null ? 'meeting' : linkFormOpen ? 'link' : null;
    if (active) {
      return (
        <div className="flex flex-col gap-[10px]" data-testid="mobile-choice-active">
          {active === 'meeting' ? <>{meetingRow}{meetingForm}</> : <>{linkRow}{linkForm}</>}
          <ActionCard Icon={Mic} title="Записать сейчас"
            description="Разговор рядом — текст по ходу речи"
            onClick={onStartLive} />
          {active === 'link' && meetingRow}
          {fileRow}
          {active === 'meeting' && linkRow}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-[10px]">
        <div className="bg-paper-card rounded-[20px] px-[22px] pt-7 pb-6 flex flex-col items-center text-center gap-[14px]">
          <h1 className="m-0 font-display text-[34px] font-extrabold leading-[1.02] tracking-[-0.035em] text-paper-ink">
            Дайте запись.
            <span className="block text-paper-ink-3">Вернём расшифровку.</span>
          </h1>
          <p className="m-0 text-[14px] leading-[1.5] text-paper-ink-2">
            Положите телефон на стол — текст появится по ходу разговора.
          </p>
          <button type="button" onClick={onStartLive}
            className="w-full h-[52px] rounded-[14px] bg-paper-accent hover:bg-paper-accent-hover text-[16px] font-semibold text-white transition-colors flex items-center justify-center gap-2">
            <Mic size={18} strokeWidth={2} /> Записать сейчас
          </button>
        </div>
        {meetingRow}
        {fileRow}
        {linkRow}
      </div>
    );
  }
  return (
  <div className="flex-1 min-h-0 flex flex-col gap-3">
    <div className="shrink-0 md:shrink md:flex-1 md:min-h-[300px] bg-paper-card rounded-[20px] md:rounded-card px-[22px] pt-7 pb-6 md:px-9 md:py-9 flex flex-col items-center justify-center text-center gap-[14px] md:gap-4">
      <h1 className="m-0 font-display text-[34px] md:text-[48px] font-extrabold leading-[1.02] tracking-[-0.035em] text-paper-ink">
        Дайте запись.
        <span className="block text-paper-ink-3">Вернём расшифровку.</span>
      </h1>
      <p className="m-0 max-w-[46ch] text-[14px] md:text-[15px] leading-[1.5] text-paper-ink-2">
        {/* Перетаскивать на телефоне нечего — там файл, голосовое или запись здесь же. */}
        <span className="md:hidden">Файл с телефона, голосовое из мессенджера или запись прямо здесь.</span>
        <span className="hidden md:inline">
          Перетащите один файл или несколько — обработаем по очереди, стерео-звонки
          определим сами, участников подпишем.
        </span>
      </p>

      {/* На телефоне одна кнопка во всю ширину: выбор там и так множественный
          (тот же input с multiple), вторая кнопка делала бы то же самое. */}
      <div className="mt-1 w-full md:w-auto flex items-center gap-3">
        <button type="button" onClick={onPickFile}
          className="w-full md:w-auto h-[52px] md:h-auto rounded-[14px] md:rounded-control bg-paper-accent hover:bg-paper-accent-hover px-[26px] md:py-[14px] text-[16px] md:text-[15px] font-semibold text-white transition-colors">
          Выбрать файл
        </button>
        <button type="button" onClick={onPickFile}
          className="hidden md:block rounded-control bg-paper-bg px-[18px] py-[14px] text-[15px] font-medium text-paper-ink">
          Несколько файлов
        </button>
      </div>

      <span className="font-mono text-[11px] text-paper-hint">
        {FORMATS} · до 4 часов · стерео определим сами
      </span>
    </div>

    <div className="grid gap-[10px] md:gap-3 md:grid-cols-3 shrink-0">
      <ActionCard Icon={Link2} title="Ссылка на видео"
        description={SUPPORTED_VIDEO_PLATFORMS}
        short="YouTube, Rutube, Kinescope…"
        onClick={onOpenLinkForm} />
      <ActionCard Icon={Video} title="Позвать бота на встречу"
        description={`${platformsShort} — зайдёт гостем и объявит о записи`}
        short={platformsShort}
        onClick={onJoinMeeting} />
      {/* С телефона чаще всего записывают разговор рядом — эта строка первая. */}
      <ActionCard Icon={Mic} title="Записать сейчас"
        description="Микрофон или звук встречи — текст появляется по ходу речи"
        short="Разговор рядом — текст по ходу речи"
        className="order-first md:order-none"
        onClick={onStartLive} />
    </div>

    {linkForm}
    {meetingForm}
  </div>
  );
};

const RecognitionScreen = ({ sources = DEFAULT_SOURCES, quotaLeft = null,
                            onOpenProtocol, onSaved, onRenamed, onOpenPricing,
                            onRecordTitle, onStarted,
                            // Каждое изменение живой записи — наверх, в App: там
                            // её сохраняет в Историю тот же эффект, что и у
                            // вкладки «Микрофон». Без этого запись с микрофона
                            // на этом экране не сохранялась никогда (31.08–23.09).
                            onLiveChange,
                            // История нужна только пути «бот на встрече»: ему некуда
                            // класть результат. По умолчанию включена — экран без
                            // этих пропсов ведёт себя как прежде.
                            historyEnabled = true, onEnableHistory,
                            // Встреча кончилась — бот уже положил запись в Историю
                            // сам, мимо вкладки. App перечитывает список: иначе
                            // запись появлялась только после перезагрузки (24.09).
                            onMeetingDone,
                            // Запрос приложения сбросить экран: удалена запись, чей
                            // результат он показывает. {seq, taskId}; обрабатывается
                            // только на фазе «готово», остальное не трогаем.
                            resetRequest,
                            // Роль и почта из токена — решают, показывать ли подсказку
                            // про приглашение в Outlook (lib/calendarInvite.js).
                            role, email }) => {
  const [phase, setPhase] = useState(PHASES.EMPTY);
  // Через ref: опрос заведён с useCallback([sources]) и держал бы первую стрелку.
  const onMeetingDoneRef = useRef(onMeetingDone);
  onMeetingDoneRef.current = onMeetingDone;
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(null);
  // Название записи по ссылке от bit-fetch — для JSON-выгрузки результата.
  const [linkTitle, setLinkTitle] = useState(null);
  const [analysis, setAnalysis] = useState({ channels: 0, oneSided: 0 });
  // Номер выбора файла: разбор каналов по прошлому файлу отбрасывается (acceptFile).
  const pickRef = useRef(0);
  const [forceMono, setForceMono] = useState(false);
  // Выбор помнится между заходами (обращение dc8fa56e): человек выставлял одно
  // и то же перед каждой записью.
  const [settings, setSettings] = useState(() => loadSpeakerSettings('file', {
    diarize: true, numSpeakers: null, minSpeakers: null, maxSpeakers: null,
  }));
  // У ссылки свой выбор и по умолчанию выключен: без галочки русская речь идёт
  // в v3, остальная — в parakeet (25 языков). Язык не запоминаем: каждый раз
  // «Авто», иначе забытый «Английский» молча испортил бы следующее русское видео.
  const [linkSettings, setLinkSettings] = useState(() => ({
    ...loadSpeakerSettings('link', {
      diarize: false, numSpeakers: null, minSpeakers: null, maxSpeakers: null,
    }),
    language: 'auto',
  }));
  useEffect(() => { saveSpeakerSettings('file', settings); }, [settings]);
  useEffect(() => { saveSpeakerSettings('link', linkSettings); }, [linkSettings]);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('Загрузка');
  // Пакет: несколько файлов разом. Каждый — своя задача через тот же источник,
  // что и одиночный файл; список живёт здесь, статусы — в элементах.
  const [pack, setPack] = useState([]);
  const [packStarting, setPackStarting] = useState(false);
  const [result, setResult] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  // Отказ по квоте отличается от остального сбоя тем, что у него есть выход:
  // прежний экран говорил «Откройте вкладку „Тарифы“», новый оставлял человека
  // один на один с текстом отказа.
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState(null);   // null — форма не открыта
  const botName = useBotName(meetingUrl !== null);
  const joiningRef = useRef(false);
  const [joining, setJoining] = useState(false);
  // Поле ссылки на видео открывается плиткой «Ссылка на видео», как в артборде.
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [liveState, setLiveStateLocal] = useState({});
  // Обёртка, а не эффект на liveState: после «Стоп» экран сам уходит в
  // «Протокол» и размонтируется, и эффект мог не успеть отдать последнее
  // состояние. Вызов прямо в сеттере отдаёт его синхронно.
  const setLiveState = useCallback((next) => {
    setLiveStateLocal(next);
    if (onLiveChange) onLiveChange(next);
  }, [onLiveChange]);
  const [startedAt, setStartedAt] = useState(null);
  // Этап от опроса, и часы оценки «сколько осталось» — с начала распознавания, а не с
  // постановки в очередь: иначе 16 минут ожидания и 6 % давали «осталось ~250 мин»
  // (ревью Fable 22.10). Своя переменная на каждый запуск — без общего состояния.
  const stageWithClock = () => {
    let recognizing = false;
    return (s) => {
      if (s === 'Распознавание' && !recognizing) {
        recognizing = true;
        setStartedAt(Date.now());
      }
      setStage(s);
    };
  };
  // Время встречи считаем своим тиком раз в секунду. Раньше оно пересчитывалось
  // только на ответе опроса — раз в три секунды, и секунды шли рывками.
  const [meetingElapsed, setMeetingElapsed] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [voiceAnswers, setVoiceAnswers] = useState({});
  // 🔴 Замок на время запуска. Каждый запуск по ссылке занимает слот bit-fetch
  // на час (лимит 2 на человека), а пока экран молчал в ожидании ответа, люди
  // жали кнопку ещё раз — и выжигали лимит за три клика, получая 429.
  const [starting, setStarting] = useState(false);
  // 🔴 Замок именно ref, а не состояние: клики идут быстрее перерисовки, и три
  // нажатия подряд читают одно и то же старое значение starting. Состояние
  // остаётся для вида кнопки, запрет на второй запуск держит ref.
  const startingRef = useRef(false);
  const [quota, setQuota] = useState(quotaLeft);
  const [cancelling, setCancelling] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  // Что сейчас считается и как это снять. Ref, а не состояние: отмену жмут
  // посреди уже запущенной работы, и перерисовки ждать нельзя.
  const runningRef = useRef(null);
  // AbortController текущей работы. Его сигнал общий с извлечением звука и
  // источником: опрос статуса проверяет его на каждом тике и обрывается, иначе
  // отменённая задача досчитывается и подставляет результат поверх пустого
  // экрана; воркер извлечения по нему гасится.
  const signalRef = useRef(null);

  const go = (event) => setPhase((current) => nextPhase(current, event));

  // Название текущей записи для шапки: до 01.09 она повторяла пункт меню и не
  // говорила, с чем человек работает.
  useEffect(() => {
    if (!onRecordTitle) return;
    if (phase === PHASES.PACK) { onRecordTitle(packSummary(pack)); return; }
    onRecordTitle(phase === PHASES.EMPTY ? null : (file?.name || url || null));
  }, [phase, file, url, pack, onRecordTitle]);
  const byChannels = shouldSplitByChannels(analysis) && !forceMono;

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  // Встречи, которые человек оставил готовиться, нажав «Новая запись»: экран
  // о них уже не рассказывает, но когда расшифровка готова, список Истории
  // должен обновиться сам (onMeetingDone) — иначе обещание «придёт в Историю
  // сама» сбывалось только после перезагрузки (ревью 05.10).
  const backgroundRef = useRef(new Map());
  const watchInBackground = (meetingId) => {
    if (!meetingId || backgroundRef.current.has(meetingId)) return;
    const timer = setInterval(async () => {
      let data = null;
      let error = null;
      try {
        data = await sources.meeting.status({ meetingId }, { get, baseUrl: '/meetbot-api' });
      } catch (err) {
        error = err;
      }
      if (!meetingPollDecision({ data, error }).stop) return;
      clearInterval(timer);
      backgroundRef.current.delete(meetingId);
      onMeetingDoneRef.current?.(meetingId);
    }, MEETING_POLL_MS);
    backgroundRef.current.set(meetingId, timer);
  };
  useEffect(() => () => {
    backgroundRef.current.forEach((timer) => clearInterval(timer));
    backgroundRef.current.clear();
  }, []);

  const pollMeeting = useCallback(async (meetingId) => {
    let data = null;
    let error = null;
    try {
      data = await sources.meeting.status({ meetingId }, { get, baseUrl: '/meetbot-api' });
    } catch (err) {
      error = err;
    }
    const { stop, patch } = meetingPollDecision({ data, error });
    if (data) {
      // Ссылку и время начала знает только экран — ответ сервиса их не несёт,
      // и раскладывать его поверх состояния значило бы их стирать.
      setMeeting((prev) => ({ ...prev, meetingId, ...data }));
    } else if (patch) {
      setMeeting((prev) => (prev ? { ...prev, ...patch } : prev));
    }
    // Кончившуюся встречу забываем и здесь: на 404 источник до своей уборки не
    // доходит, и перезагрузка вкладки сутки поднимала бы ту же мёртвую встречу.
    if (stop) {
      stopPolling();
      sources.meeting.forget?.(meetingId);
      onMeetingDoneRef.current?.(meetingId);
    }
  }, [sources]);

  useEffect(() => {
    const from = meeting?.startedAt;
    if (phase !== PHASES.MEETING || !from) { setMeetingElapsed(null); return undefined; }
    const update = () => setMeetingElapsed(Math.round((Date.now() - from) / 1000));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [phase, meeting?.startedAt]);

  // Остаток квоты нужен рельсу «Что получите». Берём тем же путём, что и
  // баннер остатка, чтобы не заводить второй способ его узнать.
  useEffect(() => {
    if (quotaLeft !== null) return;
    // 🪤 Метода getUserEmail у authService нет и не было — необязательный
    // вызов молча давал undefined, и строка «Останется» не показывалась
    // никому и никогда. Почта лежит в данных токена, так её берёт и сайдбар.
    const email = authService.getTokenData?.()?.email;
    if (!email) return;
    metricsService.getQuota(email)
      // Строку «Останется N мин» видит только тот, у кого пакет вправду
      // кончается. Сотруднику перерасход ничем не грозит, а «Останется 0 мин»
      // перед каждым запуском читается как «дальше откажут».
      .then((q) => setQuota(quotaEnforced(q) ? quotaLeftMinutes(q) : null))
      .catch(() => {});   // не знаем остаток — просто не показываем строку
  }, [quotaLeft]);

  // Встреча, начатая до перезагрузки вкладки, и мусор от оборванной живой
  // записи — разбираются при первом рендере, до всякого действия человека.
  useEffect(() => {
    sources.live.restore();
    const saved = sources.meeting.restore();
    if (saved) {
      setMeeting({ meetingId: saved.meetingId, url: saved.url, startedAt: saved.startedAt,
                   state: 'JOINING' });
      setPhase(PHASES.MEETING);
      pollMeeting(saved.meetingId);
      pollRef.current = setInterval(() => pollMeeting(saved.meetingId), MEETING_POLL_MS);
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Один файл — прежний путь с настройками; несколько — пакет.
  const acceptFiles = (list) => {
    const files = Array.from(list || []);
    if (files.length > 1) {
      const items = packItems(files);
      setPack(items); setError(null);
      go('pack_picked');
      items.forEach((it) => {
        getAudioDuration(it.file)
          .then((d) => setPack((prev) => updateItem(prev, it.id, { duration: d })))
          .catch(() => {});
      });
      return;
    }
    acceptFile(files[0]);
  };

  const acceptFile = async (picked) => {
    if (!picked) return;
    // Разбор каналов идёт секунды. Результат по прошлому файлу (выбрали звонок,
    // очистили, выбрали встречу) не должен лечь на новый — иначе встреча уйдёт
    // разделением по каналам.
    const pick = ++pickRef.current;
    setFile(picked); setError(null); setForceMono(false);
    setAnalysis({ channels: 0, oneSided: 0 });
    go('file_picked');
    // Длительность нужна и для оценки времени, и для списания квоты: без неё
    // рельс «Что получите» показывал нули.
    const duration = await getAudioDuration(picked).catch(() => null);
    if (pick !== pickRef.current) return;
    setDuration(duration);
    const result = await sources.analyze(picked, { duration });
    if (pick === pickRef.current) setAnalysis(result);
  };

  const handleStart = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setError(null); setQuotaBlocked(false); setProgress(0); setStage('Загрузка');
    go('started');
    const source = byChannels ? sources.stereo : sources.file;
    const controller = new AbortController();
    const { signal } = controller;
    signalRef.current = controller;
    // 🔴 Отмена до старта задачи. Извлечение и загрузка идут минуты, и
    // «Отменить» в это время жмут чаще всего. До 02.10 извлечение шло без
    // сигнала, а после него задача всё равно стартовала и вставала под
    // завершитель: видеокарта считала, минуты списывались, в Истории
    // появлялась запись. Поэтому сигнал проверяется на каждом шаге.
    const stopIfCancelled = () => { if (signal.aborted) throw new CancelledError(); };
    try {
      // 🔴 Из видео сначала достаём звук: полуторачасовая запись экрана весит
      // гигабайты, а её звуковая дорожка — десятки мегабайт. Без этого человек
      // ждёт загрузку впустую. Сбой извлечения не отменяет работу — грузим
      // оригинал, как делал прежний экран.
      let payload = file;
      if (sources.canExtract && sources.canExtract()) {
        setStage('Извлечение звука');
        try {
          const { file: extracted } = await sources.extractAudio(file, {
            onProgress: (p) => setProgress(Math.round(p * 100)),
            signal,
          });
          if (extracted) payload = extracted;
        } catch (e) {
          stopIfCancelled();   // AbortError воркера — это отмена, не сбой извлечения
          if (e.name === 'AudioTooLongError') { setError(e.message); go('failed'); return; }
          // Остальное — молча грузим исходный файл: экономия трафика потеряна,
          // но работа человека нет.
        }
        stopIfCancelled();   // воркер мог отдать звук раньше, чем дошёл сигнал
        setProgress(0);
        setStage('Загрузка');
      }

      stopIfCancelled();
      setVoiceAnswers({});
      const task = await source.start(
        byChannels ? { file: payload } : { file: payload, ...settings },
        { post: postUpload, baseUrls: {
          plain: ENDPOINTS.asr,
          diarize: ENDPOINTS.diarize,
        },
        baseUrl: ENDPOINTS.dual,
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))) },
      );
      const cancelDeps = { cancelTask: sources.cancelTask };
      // Отмену нажали, пока шла загрузка: снять задачу handleCancel было не по
      // чему — номер пришёл только сейчас. Снимаем её здесь и под завершитель
      // не ставим, иначе она досчитается и уедет в Историю. Загрузку на лету
      // не обрываем: сервер мог успеть завести задачу, и номер мы бы не узнали.
      if (signal.aborted) {
        try { await source.cancel(task, cancelDeps); } catch (_) { /* как в handleCancel */ }
        throw new CancelledError();
      }
      runningRef.current = { source, task, deps: cancelDeps };
      // Задача зарегистрирована под завершитель: «Вкладку можно закрыть —
      // доделаем сами» (макет). Сервис — по тому же выбору, что и источник.
      if (onStarted) {
        onStarted({
          taskId: task.taskId,
          service: byChannels ? 'dual' : (settings.diarize ? 'diarize' : 'transcribe'),
          title: file?.name || 'Распознавание',
          meta: duration ? { duration } : null,
        });
      }
      // Файл принят — дальше ждём слот; «Распознавание» включит опрос, когда
      // задачу возьмут в работу.
      setStage('Очередь');
      setStartedAt(Date.now());
      const done = await source.watch(task, {
        get,
        diarize: settings.diarize && !byChannels,
        onProgress: setProgress,
        // Ожидание слота — этап «Очередь», а не «Распознаём речь · 0 %».
        onStage: stageWithClock(),
        signal,
      });
      // Отмена во время последнего запроса результата: опрос уже прошёл, но
      // сохранять в Историю отменённое нельзя.
      stopIfCancelled();
      // 🔴 Речи в записи не нашлось — говорим об этом словами и НЕ кладём
      // пустую запись в Историю. Прежний экран так и делал
      // (TranscriptionWithoutDiarization.js:1000); этот признак терял, и
      // 08.09.2026 человек получил четыре записи Истории без единой реплики,
      // без объяснения и с подписью «Эта расшифровка останется в Истории».
      // Судим по тексту, а не по done.noSpeech: стерео-источник такого поля
      // не отдаёт вовсе (lib/recognition/sources/stereo.js).
      if (resultIsEmpty(done?.fragments || [])) {
        setError(NO_SPEECH_MESSAGE);
        go('failed');
        return;
      }
      setResult(done);
      // Результат отдаём наверх: сохранение в Историю живёт в App, как у
      // остальных экранов, иначе запись пропадёт вместе со вкладкой.
      // 🔴 Сохранение строго ДО перехода: протокол привязывается к записи по
      // ключу задачи, и уйди он первым — привязываться было бы не к чему.
      if (onSaved) onSaved({ taskId: task.taskId, done, file, duration, byChannels });
      go('done');
      openProtocol(done, file?.name || '');
    } catch (e) {
      // Отмена — не сбой: экран уже вычищен нажатием, и красная полоса поверх
      // него сообщила бы о поломке там, где человек сам попросил остановиться.
      // Оборванная после отмены загрузка — тоже отмена, хоть и с другой ошибкой.
      if (!isCancelled(e) && !signal.aborted) {
        // 429 у сервисов распознавания — это исчерпанная квота, и у неё есть
        // выход: прежний экран звал на вкладку «Тарифы», новый оставлял
        // человека один на один с текстом отказа.
        const outOfQuota = e?.response?.status === 429;
        setQuotaBlocked(outOfQuota);
        const blocked = outOfQuota ? await resolveQuotaBlockedError(e).catch(() => null) : null;
        // Слова сервера важнее «Request failed with status code 429».
        setError(blocked || e?.response?.data?.detail || unavailableText(e) || e.message
                 || 'Не удалось обработать запись');
        go('failed');
      }
    } finally {
      runningRef.current = null;
      startingRef.current = false;
      setStarting(false);
    }
  };

  // Кнопка «Позвать бота» открывает форму, а не шлёт запрос: раньше уходила
  // пустая ссылка и человек получал «Не понимаю эту ссылку на встречу».
  // Ссылку, уже вставленную в поле видео, переносим — это она и есть.
  const openMeetingForm = () => {
    setError(null);
    // Ссылку, а не вставленный текст: его форма покажет и сохранит как адрес.
    setMeetingUrl(routeLink(url).kind === 'meeting' ? extractLink(url) : '');
  };

  const handleJoinMeeting = async () => {
    // Два клика — два бота: перед /join теперь ещё сохранение имени, и окно
    // для второго нажатия стало шире.
    if (joiningRef.current) return;
    joiningRef.current = true;
    setJoining(true);
    setError(null);
    try {
      // Включаем Историю тем же нажатием: иначе bit-meetbot откажет, и человек
      // пойдёт в профиль посреди встречи (жалоба Юли Зарубиной 18.09.2026).
      if (!historyEnabled && onEnableHistory) await onEnableHistory();
      const displayName = await botName.commit();
      const task = await sources.meeting.start({ url: meetingUrl, displayName },
                                               { post, baseUrl: '/meetbot-api' });
      setMeeting({ meetingId: task.meetingId, url: task.url, state: task.state,
                   can_stop: task.can_stop, can_start_new: task.can_start_new,
                   startedAt: Date.now() });
      setMeetingUrl(null);
      go('meeting_joined');
      pollRef.current = setInterval(() => pollMeeting(task.meetingId), MEETING_POLL_MS);
    } catch (e) {
      setError(describeJoinError(e));
    } finally {
      joiningRef.current = false;
      setJoining(false);
    }
  };

  const handleStartLink = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setError(null); setQuotaBlocked(false);
    const controller = new AbortController();
    const { signal } = controller;
    signalRef.current = controller;
    try {
      const task = await sources.link.start({ url, ...linkSettings }, {
        fetch: sources.fetch, baseUrl: ENDPOINTS.fetch,
      });
      // Ссылку на встречу источник не скачивает — открываем форму бота.
      if (task.kind === 'meeting') { setMeetingUrl(task.url); return; }
      go('link_started'); setStage('Скачивание'); setStartedAt(Date.now());
      const linkDeps = {
        fetch: sources.fetch, cancelTask: sources.cancelTask,
        baseUrl: ENDPOINTS.fetch,
        asrBaseUrl: ENDPOINTS.asrParakeet,
        // «По участникам» bit-fetch отдаёт запись в diarize — статус, текст и
        // отмена тогда у него.
        diarizeBaseUrl: ENDPOINTS.diarize,
        // Русскую речь без участников — в v3, тот же сервис, что у файла.
        v3BaseUrl: ENDPOINTS.asr,
      };
      runningRef.current = { source: sources.link, task, deps: linkDeps };
      const done = await sources.link.watch(task, {
        ...linkDeps, get, signal,
        onProgress: setProgress,
        onStage: stageWithClock(),
        // Скачанное уезжает на распознавание — с этого момента отменять надо
        // уже его, и номер задачи и сервис известны только источнику.
        onTaskId: (taskId, asrBaseUrl) => { runningRef.current = {
          source: sources.link, task: { ...task, taskId, asrBaseUrl }, deps: linkDeps }; },
      });
      // То же и для видео по ссылке: пустая расшифровка — не результат.
      // Возврат на пустой экран, а не в настройки: файла у ссылки нет.
      if (resultIsEmpty(done?.fragments || [])) {
        setError(NO_SPEECH_MESSAGE);
        go('failed_no_file');
        return;
      }
      // Длительность облачного диска приходит только после скачивания — её
      // отдаёт источник; на старте у Яндекс Диска и прочих там null.
      const linkDuration = done.duration || task.duration || null;
      setDuration(linkDuration);
      setLinkTitle(task.title || null);
      setResult(done);
      if (onSaved) {
        // 🔴 Ключ — задачи распознавания, а не загрузки: тем же ключом
        // protocolHandoff привязывает Протокол к записи Истории. С fetchId
        // протокол ссылки в Историю не дописывался.
        onSaved({ taskId: done.taskId || task.fetchId, done, duration: linkDuration,
                  byChannels: false,
                  // Без названия от bit-fetch — имя хоста, не адрес целиком: название
                  // уходит в публичную ссылку «Поделиться», а в пути облачного диска
                  // лежит ключ доступа (ревью 02.10).
                  title: task.title || linkTitleFallback(task.url), sourceUrl: task.url });
      }
      go('done');
      openProtocol(done, task.title || url);
    } catch (e) {
      if (!isCancelled(e)) {
        // Квоту здесь не проверяем: источник уже перевёл ответ сервера в слова,
        // а 429 у bit-fetch — это лимит одновременных загрузок, не квота.
        setError(e.message || 'Не удалось забрать запись по ссылке');
        // Файла у ссылки нет — в фазу настроек возвращаться некуда, там пусто.
        // Ссылку в поле сохраняем: повторить надо одним кликом.
        go('failed_no_file');
      }
    } finally {
      runningRef.current = null;
      startingRef.current = false;
      setStarting(false);
    }
  };

  const handleStopMeeting = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      await sources.meeting.stop({ meetingId: meeting.meetingId }, { post, baseUrl: '/meetbot-api' });
      await pollMeeting(meeting.meetingId);   // подхватить состояние сразу
    } catch (_) {
      // best-effort: следующий тик опроса покажет актуальное состояние
    } finally {
      setStopping(false);
    }
  };

  /**
   * Ответ на вопрос узнавания. Подтверждение сохраняет голос под этим именем,
   * отказ помечает срабатывание ложным — оба ответа уходят на сервер, иначе
   * узнавание не научится и спросит то же самое в следующий раз.
   */
  const handleVoiceAnswer = async (action, item) => {
    setVoiceAnswers((prev) => ({ ...prev, [item.speakerId]: action }));
    const taskId = result?.taskId || result?.status?.task_id;
    if (!taskId) return;
    try {
      if (action === 'confirm') await voiceService.remember(taskId, { [item.speakerId]: item.name });
      else await voiceService.reject(taskId, item.speakerId);
    } catch (_) {
      // Ответ уже убран с экрана — повторно спрашивать то же самое навязчиво.
    }
  };

  /** Скачивание расшифровки файлом — имя от исходной записи, как в старом экране.
   *
   * Расширение и тип приходят от «Другие форматы» (субтитры, markdown, JSON —
   * обращение 6f371de1); главная кнопка зовёт с одним текстом, это .txt.
   */
  const handleDownload = (text, ext = 'txt', type = 'text/plain;charset=utf-8') => {
    const blob = new Blob([text], { type });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = buildDownloadName(file?.name || 'запись', 'расшифровка', ext);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  /**
   * Переименование участников. Правим показанный текст и отдаём наверх: запись
   * уже в Истории, и без этого имена остались бы только на экране (ровно тот
   * дефект, что чинили 31.08 в App.transcriptUpdate).
   */
  const handleRename = (map) => {
    const renamed = (result?.fragments || []).map((f) => (
      f.speaker && map[f.speaker] ? { ...f, speaker: map[f.speaker] } : f
    ));
    const next = { ...result, fragments: renamed };
    setResult(next);
    if (onRenamed) onRenamed(next);
  };

  /**
   * Передача готовой расшифровки во вкладку «Протокол».
   *
   * 🔴 Зовётся сама, как только расшифровка готова: так работал прежний экран
   * (`autoProtocolRef` в TranscriptionWithoutDiarization), и человек попадал в
   * протокол без лишнего нажатия. Редизайн это потерял.
   *
   * Пустую расшифровку не передаём: в «Протоколе» человеку нечего было бы
   * делать, а объяснение «речи не нашли» стоит на самом экране распознавания.
   */
  /**
   * Пакет: файлы уходят по очереди (загрузка последовательная, чтобы не
   * забить канал), задачи считаются параллельно — сколько разрешит очередь
   * сервиса (там не больше одной задачи человека в работе, пока ждут чужие).
   * Каждая задача регистрируется под завершитель сразу после старта: с этого
   * места закрытая вкладка ничего не теряет. Сбой одного файла помечает
   * только его.
   */
  const packRef = useRef(pack);
  packRef.current = pack;
  const patchPack = (id, patch) => setPack((prev) => updateItem(prev, id, patch));

  const watchPackItem = async (item, task, source) => {
    try {
      const done = await source.watch(task, {
        get, diarize: settings.diarize && !item.byChannels, onProgress: () => {}, signal: { aborted: false },
      });
      if (resultIsEmpty(done?.fragments || [])) {
        patchPack(item.id, { status: 'failed', error: NO_SPEECH_MESSAGE, retryable: false });
        return;
      }
      if (onSaved) {
        onSaved({ taskId: task.taskId, done, file: item.file, duration: item.duration,
                  byChannels: item.byChannels });
      }
      patchPack(item.id, { status: 'done', done });
    } catch (e) {
      patchPack(item.id, { status: 'failed', retryable: false,
                           error: e?.response?.data?.detail || unavailableText(e) || e?.message
                                  || 'Не удалось обработать запись' });
    }
  };

  const startPackItem = async (item) => {
    patchPack(item.id, { status: 'uploading', error: null });
    try {
      const analysis = await sources.analyze(item.file, { duration: item.duration });
      const split = shouldSplitByChannels(analysis);
      const source = split ? sources.stereo : sources.file;
      let payload = item.file;
      if (sources.canExtract && sources.canExtract()) {
        try {
          const { file: extracted } = await sources.extractAudio(item.file, { onProgress: () => {} });
          if (extracted) payload = extracted;
        } catch (e) {
          if (e.name === 'AudioTooLongError') {
            patchPack(item.id, { status: 'failed', error: e.message, retryable: false });
            return;
          }
        }
      }
      const task = await source.start(
        // Из выбора в пакет идёт только галочка: числа участников на экране
        // пакета нет, и запомненное под один звонок уехало бы во все файлы.
        split ? { file: payload } : { file: payload, diarize: settings.diarize },
        { post: postUpload, baseUrls: { plain: ENDPOINTS.asr, diarize: ENDPOINTS.diarize },
          baseUrl: ENDPOINTS.dual, onUploadProgress: () => {} },
      );
      if (onStarted) {
        onStarted({ taskId: task.taskId,
                    service: split ? 'dual' : (settings.diarize ? 'diarize' : 'transcribe'),
                    title: item.name, meta: item.duration ? { duration: item.duration } : null });
      }
      const started = { ...item, byChannels: split, taskId: task.taskId };
      patchPack(item.id, { status: 'processing', taskId: task.taskId, byChannels: split });
      watchPackItem(started, task, source);
    } catch (e) {
      const outOfQuota = e?.response?.status === 429;
      const blocked = outOfQuota ? await resolveQuotaBlockedError(e).catch(() => null) : null;
      // Загрузка оборвалась или отказ до старта — задачи нет, минуты не списаны,
      // повторить можно; исчерпанная квота — повторять бессмысленно.
      patchPack(item.id, {
        status: 'failed', retryable: !outOfQuota,
        error: blocked || e?.response?.data?.detail || unavailableText(e)
               || (e?.response ? e.message : 'Загрузка оборвалась'),
      });
      if (outOfQuota) setQuotaBlocked(true);
      return outOfQuota ? 'quota' : 'failed';
    }
    return 'started';
  };

  const handleStartPack = async () => {
    if (packStarting) return;
    setPackStarting(true); setError(null); setQuotaBlocked(false);
    try {
      for (const item of packRef.current) {
        if (item.status !== 'queued') continue;
        // eslint-disable-next-line no-await-in-loop
        const outcome = await startPackItem(item);
        if (outcome === 'quota') {
          // Квота кончилась — остальные заливать незачем: каждый файл уехал бы
          // на сервер целиком ради того же 429. Помечаем без загрузки.
          setPack((prev) => prev.map((it) => (it.status === 'queued'
            ? { ...it, status: 'failed', error: 'Квота исчерпана', retryable: false } : it)));
          break;
        }
      }
    } finally {
      setPackStarting(false);
    }
  };

  const handleRetryPackItem = (id) => {
    const item = packRef.current.find((it) => it.id === id);
    if (item) startPackItem(item);
  };

  const openProtocol = useCallback((done, name) => {
    if (!onOpenProtocol) return;
    const h = protocolHandoff(done, { fileName: name });
    if (!h.text) return;
    onOpenProtocol(h.text, h.rawText, h.fileName, h.taskId, h.speakerIdByLabel,
                   h.recognizedSpeakers);
  }, [onOpenProtocol]);

  /**
   * Отмена работы.
   *
   * 🔴 Сперва сервер, потом экран. Кнопка до 01.09 только чистила страницу:
   * видеокарта продолжала считать, минуты списывались с квоты, а запись потом
   * тихо появлялась в Истории — человек был уверен, что остановил работу.
   *
   * Сбой самой отмены человеку не показываем, но экран отпускаем в любом
   * случае: держать его в фазе обработки из-за отвалившейся сети — значит
   * запереть работу второй раз.
   *
   * Задачи на сервере ещё может не быть — идёт извлечение звука или загрузка.
   * Тогда снимать здесь нечего: сигнал гасит извлечение, а задачу, номер
   * которой вернётся после нажатия, снимает сам handleStart и под завершитель
   * её не ставит.
   */
  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    if (signalRef.current) signalRef.current.abort();
    const running = runningRef.current;
    runningRef.current = null;
    try {
      if (running?.source?.cancel) await running.source.cancel(running.task, running.deps);
    } catch (_) {
      // Задача могла закончиться сама или быть подчищенной по TTL — жаловаться
      // на неудачную отмену не за что.
    } finally {
      setCancelling(false);
      handleReset();
    }
  };

  /**
   * Выход из живой записи. Пока идёт запись, спрашиваем: уход размонтирует
   * поток и обрывает запись; записанное уходит в Историю.
   */
  const handleLeaveLive = () => {
    // finishing — «Стоп» нажат, но накопленный за обрыв звук ещё досылается.
    // Уход отдаёт запись в Историю остановленной (StreamingTranscription, 19.10.2026).
    if (!leaveLiveAllowed(liveState, { historyEnabled })) return;
    go('reset');
  };

  const handleReset = () => {
    stopPolling();
    if (meeting?.meetingId && meeting.state === 'PROCESSING') watchInBackground(meeting.meetingId);
    if (meeting?.meetingId) sources.meeting.forget?.(meeting.meetingId);
    setFile(null); setUrl(''); setResult(null); setMeeting(null); setError(null);
    setPack([]);
    setMeetingUrl(null); setDuration(null); setLinkTitle(null); setProgress(0); setStartedAt(null);
    pickRef.current += 1;
    setAnalysis({ channels: 0, oneSided: 0 }); setForceMono(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    go('reset');
  };

  // Запрос сброса от приложения: ref-охрана, как handledOpenSeq в AppSidebar. Первый
  // показ с уже выставленным seq запросом не считается; в занятой фазе запрос
  // гасится целиком — размонтировать экран нельзя, оборвётся микрофон и встреча.
  const handledResetSeq = useRef(resetRequest ? resetRequest.seq : null);
  useEffect(() => {
    if (!resetRequest || resetRequest.seq === handledResetSeq.current) return;
    handledResetSeq.current = resetRequest.seq;
    if (shouldResetOnRequest(phase, result?.taskId, resetRequest.taskId)) handleReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRequest?.seq]);

  // Перетаскивание работает по всему экрану, а не только по дроп-зоне: человек
  // роняет файл туда, куда смотрит, и попадание в рамку — не его забота.
  const onDragOver = (e) => { e.preventDefault(); if (phase === PHASES.EMPTY) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (phase === PHASES.EMPTY && e.dataTransfer?.files?.length) acceptFiles(e.dataTransfer.files);
  };

  return (
    <div className={`relative flex-1 min-h-0 flex flex-col font-ui rounded-card ${
      dragOver ? 'ring-2 ring-inset ring-paper-accent' : ''}`}
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {/* Подсказка поверх области, а не тонкая рамка по краю экрана: на
          большом мониторе рамку легко не заметить и отпустить файл мимо. */}
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-card bg-paper-bg/85">
          <span className="rounded-control bg-paper-card px-6 py-4 font-display text-[18px] font-bold text-paper-ink">
            Отпустите файл — начнём
          </span>
        </div>
      )}
      <input type="file" ref={fileInputRef} className="hidden" accept="audio/*,video/*,.aac,.mkv" multiple
        onChange={(e) => acceptFiles(e.target.files)} />

      {/* role="alert" — не украшение: без живой области незрячий человек не
          узнаёт, что работа сорвалась, экран для него просто замолкает. */}
      {error && (
        <div role="alert"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-panel bg-paper-accent/[0.06] px-4 py-3 text-[13px] text-paper-accent shrink-0">
          <span>{error}</span>
          {quotaBlocked && onOpenPricing && (
            <button type="button" onClick={onOpenPricing}
              className="font-semibold underline underline-offset-[3px] hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
              Перейти к тарифам
            </button>
          )}
        </div>
      )}

      {phase === PHASES.EMPTY && (
        <EmptyState url={url} onUrlChange={setUrl} starting={starting}
          onPickFile={() => fileInputRef.current?.click()}
          onStartLink={handleStartLink}
          onOpenLinkForm={() => { setMeetingUrl(null); setLinkFormOpen(true); }}
          linkFormOpen={linkFormOpen}
          onCloseLinkForm={() => { setLinkFormOpen(false); setUrl(''); }}
          linkSettings={linkSettings} onLinkSettingsChange={setLinkSettings}
          onJoinMeeting={() => { setLinkFormOpen(false); openMeetingForm(); }}
          meetingUrl={meetingUrl} onMeetingUrlChange={setMeetingUrl} onSendBot={handleJoinMeeting}
          onCloseMeetingForm={() => setMeetingUrl(null)}
          historyEnabled={historyEnabled} botName={botName} joining={joining}
          calendarHint={showCalendarHint(role, email)}
          onStartLive={() => go('live_started')} />
      )}

      {phase === PHASES.PACK && (
        <PackState items={pack} diarize={settings.diarize}
          onDiarizeChange={(diarize) => setSettings((prev) => ({ ...prev, diarize }))}
          onStart={handleStartPack} starting={packStarting}
          onRemove={(id) => setPack((prev) => removeItem(prev, id))}
          onRetry={handleRetryPackItem}
          onOpen={(it) => openProtocol(it.done, it.name)}
          onNew={handleReset} historyEnabled={historyEnabled} />
      )}

      {phase === PHASES.UPLOADED && file && (
        <ReadyState file={file} duration={duration} analysis={forceMono ? { channels: 1, oneSided: 0 } : analysis}
          settings={settings} quotaLeft={quota} historyEnabled={historyEnabled}
          onChange={setSettings} onStart={handleStart} starting={starting}
          onForceMono={() => setForceMono(true)} onClear={handleReset} />
      )}

      {/* Живая запись — существующий экран потокового распознавания: он уже
          умеет микрофон, паузу и дописывание текста, и переписывать его заново
          ради новой обёртки незачем. */}
      {phase === PHASES.LIVE && (
        /* 🔴 Выход. До 01.09 из фазы микрофона не было ни одной кнопки
           возврата: единственным способом уйти была перезагрузка страницы.
           Уход размонтирует поток — микрофон и сокет освобождаются сами.
           Кнопка живёт внутри экрана записи: в артборде «К выбору записи» —
           последняя строка карточки, а не панель над ней. */
        <StreamingTranscription state={liveState} setState={setLiveState}
          onOpenProtocol={onOpenProtocol} onLeave={handleLeaveLive} />
      )}

      {phase === PHASES.MEETING && (
        <MeetingState state={meeting?.state} error={meeting?.error} url={meeting?.url}
          canStop={meetingCanStop(meeting)} canStartNew={meetingCanStartNew(meeting)}
          elapsedSec={meetingElapsed}
          onStop={handleStopMeeting}
          stopping={stopping} onNew={handleReset} />
      )}

      {phase === PHASES.PROCESSING && (
        <ProcessingState fileName={file?.name || url || 'запись'} stage={stage} progress={progress}
          kind={file ? (byChannels ? 'stereo' : 'file') : 'link'}
          historyEnabled={historyEnabled}
          // Оценка — из уже прошедшего времени и доли работы: до 5% она врёт
          // слишком сильно, поэтому там честнее её не показывать вовсе.
          etaSeconds={stage === 'Распознавание' && startedAt && progress > 5
            ? Math.round(((Date.now() - startedAt) / 1000 / progress) * (100 - progress))
            : null}
          byChannels={byChannels} onCancel={handleCancel} cancelling={cancelling} />
      )}

      {phase === PHASES.DONE && result && (
        <ResultState fragments={result.fragments || []} kind={byChannels ? 'stereo' : 'file'}
          meta={{ duration, model: resultModel(file, byChannels, result),
                  // Название — для JSON-выгрузки: имя файла или название от
                  // bit-fetch. Без него у ссылки только хост: в пути облачного
                  // диска лежит ключ доступа (linkTitleFallback).
                  title: file?.name || linkTitle || (url ? linkTitleFallback(url) : null) }}
          voice={voiceQuestions(result?.status, voiceAnswers)}
          onVoiceAnswer={handleVoiceAnswer}
          onDownload={handleDownload}
          onRename={handleRename}
          onCreateProtocol={() => openProtocol(result, file?.name || url || '')}
          onNew={handleReset} />
      )}
    </div>
  );
};

/**
 * Точка входа наружу — за флагом сборки. Гейт отдельным компонентом, а не
 * ранним return внутри RecognitionScreen: иначе условие встало бы перед хуками.
 */
const RecognitionScreenGate = (props) => (RECOGNITION_V2 ? <RecognitionScreen {...props} /> : null);

/**
 * Модель в рельсе результата. Ссылку без участников bit-fetch отдаёт в GigaAM
 * (русская речь) или в parakeet (другие языки) — кому именно, говорит engine.
 */
function resultModel(file, byChannels, result) {
  if (byChannels) return 'Стерео · по каналам';
  if (file) return 'GigaAM v3';
  if (result?.engine === 'v3') return 'GigaAM v3';
  return (result?.fragments || []).some((f) => f.speakerId) ? 'GigaAM v3' : 'Parakeet v3';
}

/** Название записи по ссылке, когда bit-fetch его не отдал: хост, без пути и ключей. */
export function linkTitleFallback(url) {
  try {
    return `Запись с ${new URL(url).hostname}`;
  } catch {
    return 'Запись по ссылке';
  }
}

export default RecognitionScreenGate;
